import { useEffect, useMemo, useState } from "react";
import {
  SecretField,
  Alert,
  Button,
  Card,
  Checkbox,
  DataTable,
  Dialog,
  EmptyState,
  Field,
  Grid,
  Inline,
  KpiCard,
  MoneyField,
  PageHeader,
  Panel,
  SelectField,
  Spinner,
  Stack,
  StatusBadge,
  Switch,
  Tab,
  Tabs,
  TextAreaField,
  formatMoneyMinor,
  useToast
} from "@leylion/design-system";
import {
  assignUserRole,
  createRole,
  getApprovalPolicies,
  deleteUserRoleAssignment,
  getBranches,
  getEmployees,
  getPermissionGroups,
  getRole,
  getRoles,
  getRuntimeSettings,
  getSecurityOverview,
  getSecuritySessions,
  getUsers,
  resetUserMfa,
  resetUserPassword,
  resetUserPin,
  revokeSecuritySession,
  saveApprovalPolicy,
  saveEmployee,
  setUserStatus,
  startMfaEnrollment,
  updateRole,
  verifyMfaEnrollment
} from "../api/client";
import type { ContextOption } from "../api/types";
import { validateNewPin } from "../security/pin-policy";
import type { Branch } from "../api/settings-types";
import type {
  ApprovalOperation,
  ApprovalPolicy,
  Employee,
  EmploymentStatus,
  PermissionGroup,
  RoleDetail,
  RoleSummary,
  SecurityOverview,
  SecuritySession,
  UserAccess
} from "../api/identity-types";
import "./team.css";

type Language = "ar" | "en";
type TeamView = "overview" | "employees" | "roles" | "access" | "sessions" | "approvals";

function getTeamAccessErrorMessage(
  caught: unknown,
  ar: boolean,
  fallbackAr: string,
  fallbackEn: string
) {
  const candidate = caught as {
    code?: unknown;
    message?: unknown;
    problem?: { title?: unknown };
  } | null;
  const code = typeof candidate?.code === "string"
    ? candidate.code
    : typeof candidate?.problem?.title === "string"
      ? candidate.problem.title
      : "";
  const message = typeof candidate?.message === "string" ? candidate.message : "";

  const localizedByCode: Record<string, [string, string]> = {
    USER_SETUP_INCOMPLETE: [
      "أكمل بريد الدخول وكلمة المرور المؤقتة والدور والفرع قبل إنشاء الحساب.",
      "Complete the login email, temporary password, role and branch before creating the account."
    ],
    PASSWORD_POLICY_FAILED: [
      "يجب أن تتكون كلمة المرور المؤقتة من 10 أحرف على الأقل، وتتضمن حرفًا كبيرًا وصغيرًا ورقمًا ورمزًا.",
      "The temporary password must be at least 10 characters and include upper- and lower-case letters, a number and a symbol."
    ],
    USER_EMAIL_EXISTS: [
      "بريد الدخول مستخدم بالفعل لحساب آخر.",
      "The login email is already used by another account."
    ],
    PIN_POLICY_FAILED: [
      "يجب أن يتكوّن رمز PIN من 4 إلى 8 أرقام فقط.",
      "PIN must contain 4 to 8 digits only."
    ],
    PIN_TOO_WEAK: [
      "لا يمكن استخدام PIN بأرقام متكررة أو متسلسلة. اختر أرقامًا غير متتابعة.",
      "Sequential or repeated-digit PINs are not allowed. Choose a non-sequential PIN."
    ]
  };

  const localized = localizedByCode[code];
  if (localized) return ar ? localized[0] : localized[1];

  if (/plan limit for users would be exceeded/i.test(message)) {
    return ar
      ? "تعذر إنشاء حساب الدخول لأن الحد الأقصى لمستخدمي الخطة قد تم بلوغه. يُحتسب حساب المالك ضمن هذا الحد."
      : "The login account cannot be created because the plan user limit has been reached. The owner account counts toward this limit.";
  }
  if (/sequential or repeated-digit pins are not allowed/i.test(message)) {
    return ar
      ? "لا يمكن استخدام PIN بأرقام متكررة أو متسلسلة. اختر أرقامًا غير متتابعة."
      : "Sequential or repeated-digit PINs are not allowed. Choose a non-sequential PIN.";
  }
  if (/pin must contain 4 to 8 digits/i.test(message)) {
    return ar ? "يجب أن يتكوّن رمز PIN من 4 إلى 8 أرقام فقط." : "PIN must contain 4 to 8 digits only.";
  }

  return message || (ar ? fallbackAr : fallbackEn);
}

function validateTemporaryPassword(password: string, ar: boolean) {
  const valid = password.length >= 10
    && /\p{Lu}/u.test(password)
    && /\p{Ll}/u.test(password)
    && /\p{Nd}/u.test(password)
    && /[^\p{L}\p{Nd}]/u.test(password);
  if (valid) return "";
  return ar
    ? "يجب أن تتكون كلمة المرور المؤقتة من 10 أحرف على الأقل، وتتضمن حرفًا كبيرًا وصغيرًا ورقمًا ورمزًا."
    : "The temporary password must be at least 10 characters and include upper- and lower-case letters, a number and a symbol.";
}


export function TeamCenter({
  language,
  context,
  permissions,
  onOpenManagerApprovals
}: {
  language: Language;
  context: ContextOption;
  permissions: string[];
  onOpenManagerApprovals: () => void;
}) {
  const [view, setView] = useState<TeamView>("overview");
  const [overview, setOverview] = useState<SecurityOverview | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const ar = language === "ar";

  useEffect(() => {
    getSecurityOverview().then(setOverview).catch(() => setOverview(null));
  }, [refreshKey]);

  const can = (permission: string) => permissions.includes(permission);
  const navigation = [
    ["overview", ar ? "نظرة عامة" : "Overview", true],
    ["employees", ar ? "الموظفون" : "Employees", can("team.read")],
    ["roles", ar ? "الأدوار والصلاحيات" : "Roles & permissions", can("roles.read")],
    ["access", ar ? "الحسابات والوصول" : "Accounts & access", can("users.read")],
    ["sessions", ar ? "الجلسات والأمان" : "Sessions & security", can("security.read")],
    ["approvals", ar ? "الموافقات" : "Approvals", can("approvals.read")]
  ] as const;

  return (
    <Stack>
      <PageHeader
        eyebrow={ar ? "إدارة الهوية والوصول" : "Identity & Access Management"}
        title={ar ? "الفريق والأدوار والأمان" : "Team, Roles & Security"}
        description={
          ar
            ? "إدارة دورة حياة الموظف، حسابات الدخول، صلاحيات الفروع، MFA وPIN والجلسات والموافقات الإدارية."
            : "Manage the employee lifecycle, login accounts, branch access, MFA, PIN, sessions and administrative approvals."
        }
      />

      <div className="team-layout">
        <aside className="team-navigation">
          {navigation.filter(([, , visible]) => visible).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={view === key ? "is-active" : ""}
              onClick={() => setView(key)}
            >
              {label}
              {key === "approvals" && overview?.pendingApprovals ? (
                <StatusBadge tone="warning">{overview.pendingApprovals}</StatusBadge>
              ) : null}
            </button>
          ))}
        </aside>

        <main className="team-content">
          {view === "overview" ? (
            <SecurityOverviewView
              overview={overview}
              language={language}
              onOpen={setView}
            />
          ) : view === "employees" ? (
            <EmployeesPanel
              language={language}
              canManage={can("team.manage")}
              onChanged={() => setRefreshKey((value) => value + 1)}
            />
          ) : view === "roles" ? (
            <RolesPanel
              language={language}
              canManage={can("roles.manage")}
              onChanged={() => setRefreshKey((value) => value + 1)}
            />
          ) : view === "access" ? (
            <AccessPanel
              language={language}
              canManageUsers={can("users.manage")}
              canManageRoles={can("roles.manage")}
              canManageSecurity={can("security.manage")}
              onChanged={() => setRefreshKey((value) => value + 1)}
            />
          ) : view === "sessions" ? (
            <SessionsPanel
              language={language}
              canManage={can("security.manage")}
              onChanged={() => setRefreshKey((value) => value + 1)}
            />
          ) : (
            <ApprovalsGovernancePanel
              language={language}
              context={context}
              canManagePolicies={can("approvals.policies.manage")}
              onOpen={onOpenManagerApprovals}
              onChanged={() => setRefreshKey((value) => value + 1)}
            />
          )}
        </main>
      </div>
    </Stack>
  );
}

function SecurityOverviewView({
  overview,
  language,
  onOpen
}: {
  overview: SecurityOverview | null;
  language: Language;
  onOpen: (view: TeamView) => void;
}) {
  const ar = language === "ar";
  if (!overview) {
    return (
      <Card>
        <Inline>
          <Spinner label={ar ? "جارٍ التحميل" : "Loading"} />
          <span>{ar ? "جارٍ حساب حالة الأمان…" : "Calculating security posture…"}</span>
        </Inline>
      </Card>
    );
  }

  return (
    <Stack>
      <Grid columns={4}>
        <KpiCard label={ar ? "الموظفون" : "Employees"} value={overview.employees} />
        <KpiCard label={ar ? "المستخدمون النشطون" : "Active users"} value={overview.activeUsers} />
        <KpiCard
          label={ar ? "MFA مفعّل" : "MFA enabled"}
          value={`${overview.mfaEnabledUsers}/${overview.activeUsers}`}
          trendTone={overview.mfaEnabledUsers === overview.activeUsers ? "success" : "warning"}
          trend={overview.mfaEnabledUsers === overview.activeUsers ? (ar ? "مكتمل" : "Complete") : (ar ? "يحتاج إكمالًا" : "Needs attention")}
        />
        <KpiCard
          label={ar ? "الجلسات النشطة" : "Active sessions"}
          value={overview.activeSessions}
        />
        <KpiCard
          label={ar ? "الحسابات المقفلة" : "Locked users"}
          value={overview.lockedUsers}
          trendTone={overview.lockedUsers ? "danger" : "success"}
        />
        <KpiCard
          label={ar ? "الموافقات المعلقة" : "Pending approvals"}
          value={overview.pendingApprovals}
          trendTone={overview.pendingApprovals ? "warning" : "success"}
        />
        <KpiCard label={ar ? "الأدوار المخصصة" : "Custom roles"} value={overview.customRoles} />
      </Grid>

      {overview.warnings.length ? (
        <Alert tone="warning" title={ar ? "ملاحظات أمنية" : "Security observations"}>
          {overview.warnings.join(" · ")}
        </Alert>
      ) : (
        <Alert tone="success" title={ar ? "لا توجد ملاحظات حرجة" : "No critical observations"}>
          {ar ? "ضوابط الوصول الأساسية ضمن النطاق الحالي بحالة جيدة." : "Core access controls in the current scope are healthy."}
        </Alert>
      )}

      <Grid columns={3}>
        <Card
          interactive
          title={ar ? "دورة حياة الموظف" : "Employee lifecycle"}
          description={ar ? "السجل الوظيفي والحساب والدور والفرع." : "Employment record, account, role and branch."}
          onClick={() => onOpen("employees")}
        />
        <Card
          interactive
          title={ar ? "مبدأ أقل صلاحية" : "Least privilege"}
          description={ar ? "مصفوفة صلاحيات قابلة للتدقيق مع أدوار نظام محمية." : "Auditable permission matrix with protected system roles."}
          onClick={() => onOpen("roles")}
        />
        <Card
          interactive
          title={ar ? "موافقات حساسة" : "Sensitive approvals"}
          description={ar ? "منع الموافقة الذاتية وحدود وصلاحيات حسب العملية." : "No self-approval, thresholds and operation permissions."}
          onClick={() => onOpen("approvals")}
        />
      </Grid>
    </Stack>
  );
}

function EmployeesPanel({
  language,
  canManage,
  onChanged
}: {
  language: Language;
  canManage: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const ar = language === "ar";
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<EmploymentStatus | "">("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [employeeItems, branchItems, roleItems] = await Promise.all([
        getEmployees(search || undefined, status || undefined),
        getBranches(),
        getRoles()
      ]);
      setEmployees(employeeItems);
      setBranches(branchItems);
      setRoles(roleItems.filter((role) => role.isActive));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to load employees.");
    }
  };

  useEffect(() => {
    void load();
  }, [status]);

  const openNew = () => {
    setError("");
    setEditing(null);
    setForm({
      employeeNumber: `EMP-${String(employees.length + 1).padStart(4, "0")}`,
      firstName: "",
      lastName: "",
      preferredName: "",
      jobTitle: "",
      department: "",
      phone: "",
      email: "",
      status: "Active",
      employmentType: "FullTime",
      hireDate: new Date().toISOString().slice(0, 10),
      terminationDate: null,
      primaryBranchId: branches[0]?.id ?? null,
      notes: "",
      createUserAccount: false,
      loginEmail: "",
      temporaryPassword: "",
      roleId: roles.find((role) => role.code === "CASHIER")?.id ?? roles[0]?.id ?? null,
      branchId: branches[0]?.id ?? null,
      expectedVersion: null
    });
    setEditorOpen(true);
  };

  const openEdit = (employee: Employee) => {
    setError("");
    setEditing(employee);
    setForm({
      employeeNumber: employee.employeeNumber,
      firstName: employee.firstName,
      lastName: employee.lastName,
      preferredName: employee.preferredName ?? "",
      jobTitle: employee.jobTitle ?? "",
      department: employee.department ?? "",
      phone: employee.phone ?? "",
      email: employee.email ?? "",
      status: employee.status,
      employmentType: employee.employmentType,
      hireDate: employee.hireDate,
      terminationDate: employee.terminationDate,
      primaryBranchId: employee.primaryBranchId,
      notes: "",
      createUserAccount: !employee.hasLogin,
      loginEmail: employee.email ?? "",
      temporaryPassword: "",
      roleId: employee.roleId ?? roles[0]?.id ?? null,
      branchId: employee.accessBranchId ?? employee.primaryBranchId ?? branches[0]?.id ?? null,
      expectedVersion: employee.version
    });
    setEditorOpen(true);
  };

  const save = async () => {
    setError("");
    if (form.createUserAccount) {
      const loginEmail = String(form.loginEmail ?? "").trim();
      const temporaryPassword = String(form.temporaryPassword ?? "");
      if (!loginEmail || !temporaryPassword || !form.roleId || !form.branchId) {
        setError(ar
          ? "أكمل بريد الدخول وكلمة المرور المؤقتة والدور والفرع قبل إنشاء الحساب."
          : "Complete the login email, temporary password, role and branch before creating the account.");
        return;
      }
      const passwordError = validateTemporaryPassword(temporaryPassword, ar);
      if (passwordError) {
        setError(passwordError);
        return;
      }
    }

    try {
      await saveEmployee(editing?.id ?? null, {
        ...form,
        preferredName: form.preferredName || null,
        jobTitle: form.jobTitle || null,
        department: form.department || null,
        phone: form.phone || null,
        email: form.email || null,
        terminationDate: form.terminationDate || null,
        notes: form.notes || null,
        loginEmail: form.loginEmail || null,
        temporaryPassword: form.temporaryPassword || null
      });
      setEditorOpen(false);
      toast.notify({ tone: "success", title: ar ? "تم حفظ الموظف" : "Employee saved" });
      await load();
      onChanged();
    } catch (caught) {
      setError(getTeamAccessErrorMessage(caught, ar, "تعذر حفظ الموظف.", "Unable to save employee."));
    }
  };

  return (
    <Stack>
      <Panel
        title={ar ? "الموظفون" : "Employees"}
        description={ar ? "سجل وظيفي موحد مع حالة العمل وحساب الدخول والدور والفرع." : "Unified employment record with work status, login account, role and branch."}
        actions={canManage ? <Button onClick={openNew}>{ar ? "إضافة موظف" : "Add employee"}</Button> : null}
      >
        <Stack>
          {error ? <Alert tone="danger" title={ar ? "تعذر التنفيذ" : "Action failed"}>{error}</Alert> : null}
          <div className="team-filters">
            <Field
              label={ar ? "بحث" : "Search"}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              onKeyDown={(event) => event.key === "Enter" && void load()}
            />
            <SelectField
              label={ar ? "الحالة" : "Status"}
              value={status}
              onChange={(event) => setStatus(event.target.value as EmploymentStatus | "")}
            >
              <option value="">{ar ? "جميع الحالات" : "All statuses"}</option>
              {["Active", "OnLeave", "Suspended", "Terminated"].map((value) => <option key={value}>{value}</option>)}
            </SelectField>
            <Button variant="secondary" onClick={() => void load()}>{ar ? "تطبيق" : "Apply"}</Button>
          </div>

          <DataTable
            rows={employees}
            rowKey={(row) => row.id}
            emptyTitle={ar ? "لا يوجد موظفون" : "No employees"}
            columns={[
              {
                key: "employee",
                header: ar ? "الموظف" : "Employee",
                cell: (row) => (
                  <button className="team-table-link" type="button" onClick={() => openEdit(row)}>
                    <strong>{row.displayName}</strong>
                    <small>{row.employeeNumber} · {row.jobTitle ?? "—"}</small>
                  </button>
                )
              },
              {
                key: "status",
                header: ar ? "الحالة" : "Status",
                cell: (row) => <StatusBadge tone={row.status === "Active" ? "success" : row.status === "Suspended" ? "danger" : "warning"}>{row.status}</StatusBadge>
              },
              {
                key: "access",
                header: ar ? "الوصول" : "Access",
                cell: (row) => row.hasLogin ? (
                  <span><strong>{row.roleName ?? "—"}</strong><small className="team-subtext">{row.mfaEnabled ? "MFA" : ar ? "بدون MFA" : "No MFA"}</small></span>
                ) : <StatusBadge tone="neutral">{ar ? "لا يوجد حساب" : "No account"}</StatusBadge>
              },
              {
                key: "contact",
                header: ar ? "التواصل" : "Contact",
                cell: (row) => <span>{row.email ?? row.phone ?? "—"}</span>
              },
              {
                key: "updated",
                header: ar ? "آخر تحديث" : "Updated",
                cell: (row) => new Date(row.updatedAt).toLocaleDateString(ar ? "ar-AE" : "en-AE")
              }
            ]}
          />
        </Stack>
      </Panel>

      <Dialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        title={editing ? (ar ? "تعديل الموظف" : "Edit employee") : (ar ? "إضافة موظف" : "Add employee")}
        footer={
          <Inline>
            <Button variant="secondary" onClick={() => setEditorOpen(false)}>{ar ? "إلغاء" : "Cancel"}</Button>
            <Button disabled={!canManage || !form.firstName || !form.lastName || !form.employeeNumber} onClick={() => void save()}>{ar ? "حفظ" : "Save"}</Button>
          </Inline>
        }
      >
        <Stack>
          {error ? <Alert tone="danger" title={ar ? "تعذر حفظ الموظف" : "Unable to save employee"}>{error}</Alert> : null}
          <Grid columns={2}>
            <Field label={ar ? "رقم الموظف" : "Employee number"} value={String(form.employeeNumber ?? "")} onChange={(e) => setForm({ ...form, employeeNumber: e.target.value.toUpperCase() })} />
            <SelectField label={ar ? "الحالة" : "Status"} value={String(form.status ?? "Active")} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {["Active", "OnLeave", "Suspended", "Terminated"].map((value) => <option key={value}>{value}</option>)}
            </SelectField>
            <Field label={ar ? "الاسم الأول" : "First name"} value={String(form.firstName ?? "")} onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            <Field label={ar ? "اسم العائلة" : "Last name"} value={String(form.lastName ?? "")} onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            <Field label={ar ? "الاسم المفضل" : "Preferred name"} value={String(form.preferredName ?? "")} onChange={(e) => setForm({ ...form, preferredName: e.target.value })} />
            <Field label={ar ? "المسمى الوظيفي" : "Job title"} value={String(form.jobTitle ?? "")} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} />
            <Field label={ar ? "القسم" : "Department"} value={String(form.department ?? "")} onChange={(e) => setForm({ ...form, department: e.target.value })} />
            <SelectField label={ar ? "نوع التوظيف" : "Employment type"} value={String(form.employmentType ?? "FullTime")} onChange={(e) => setForm({ ...form, employmentType: e.target.value })}>
              {["FullTime", "PartTime", "Contract", "Temporary"].map((value) => <option key={value}>{value}</option>)}
            </SelectField>
            <Field label={ar ? "الهاتف" : "Phone"} value={String(form.phone ?? "")} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <Field label={ar ? "البريد" : "Email"} type="email" value={String(form.email ?? "")} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            <Field label={ar ? "تاريخ التعيين" : "Hire date"} type="date" value={String(form.hireDate ?? "")} onChange={(e) => setForm({ ...form, hireDate: e.target.value || null })} />
            <SelectField label={ar ? "الفرع الرئيسي" : "Primary branch"} value={String(form.primaryBranchId ?? "")} onChange={(e) => setForm({ ...form, primaryBranchId: e.target.value || null, branchId: e.target.value || null })}>
              {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
            </SelectField>
          </Grid>
          {!editing?.hasLogin ? (
            <Card title={ar ? "حساب الدخول" : "Login account"}>
              <Stack>
                <Switch label={ar ? "إنشاء حساب مستخدم" : "Create user account"} checked={Boolean(form.createUserAccount)} onChange={(e) => setForm({ ...form, createUserAccount: e.target.checked })} />
                {form.createUserAccount ? (
                  <Stack>
                    <Alert tone="info" title={ar ? "حساب مستخدم ضمن حدود الخطة" : "User account plan capacity"}>
                      {ar
                        ? "إنشاء حساب دخول يستهلك مقعد مستخدم من الخطة، ويُحتسب حساب المالك ضمن الحد."
                        : "Creating a login account consumes one plan user seat, and the owner account counts toward the limit."}
                    </Alert>
                    <Grid columns={2}>
                      <Field label={ar ? "بريد الدخول" : "Login email"} type="email" value={String(form.loginEmail ?? "")} onChange={(e) => setForm({ ...form, loginEmail: e.target.value })} />
                      <SecretField label={ar ? "كلمة مرور مؤقتة" : "Temporary password"} value={String(form.temporaryPassword ?? "")} onChange={(e) => setForm({ ...form, temporaryPassword: e.target.value })} />
                      <SelectField label={ar ? "الدور" : "Role"} value={String(form.roleId ?? "")} onChange={(e) => setForm({ ...form, roleId: e.target.value })}>
                        {roles.map((role) => <option key={role.id} value={role.id}>{ar ? role.nameAr : role.nameEn}</option>)}
                      </SelectField>
                    </Grid>
                    <small className="team-subtext">
                      {ar
                        ? "كلمة المرور: 10 أحرف على الأقل مع حرف كبير وصغير ورقم ورمز."
                        : "Password: at least 10 characters with upper- and lower-case letters, a number and a symbol."}
                    </small>
                  </Stack>
                ) : null}
              </Stack>
            </Card>
          ) : null}
        </Stack>
      </Dialog>
    </Stack>
  );
}

function RolesPanel({
  language,
  canManage,
  onChanged
}: {
  language: Language;
  canManage: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const ar = language === "ar";
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [groups, setGroups] = useState<PermissionGroup[]>([]);
  const [detail, setDetail] = useState<RoleDetail | null>(null);
  const [newRole, setNewRole] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [roleItems, permissionGroups] = await Promise.all([getRoles(), getPermissionGroups()]);
      setRoles(roleItems);
      setGroups(permissionGroups);
      if (!detail && roleItems[0]) setDetail(await getRole(roleItems[0].id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to load roles.");
    }
  };

  useEffect(() => { void load(); }, []);

  const select = async (role: RoleSummary) => {
    setDetail(await getRole(role.id));
    setNewRole(false);
  };

  const createNew = () => {
    setNewRole(true);
    setDetail({
      role: {
        id: "",
        code: "CUSTOM_ROLE",
        nameAr: "دور مخصص",
        nameEn: "Custom Role",
        descriptionAr: null,
        descriptionEn: null,
        isSystem: false,
        isActive: true,
        permissionCount: 0,
        assignmentCount: 0,
        version: 0,
        updatedAt: new Date().toISOString()
      },
      permissions: []
    });
  };

  const save = async () => {
    if (!detail) return;
    try {
      const saved = newRole
        ? await createRole({
            code: detail.role.code,
            nameAr: detail.role.nameAr,
            nameEn: detail.role.nameEn,
            descriptionAr: detail.role.descriptionAr,
            descriptionEn: detail.role.descriptionEn,
            isActive: detail.role.isActive,
            permissions: detail.permissions
          })
        : await updateRole(detail);
      setDetail(saved);
      setNewRole(false);
      toast.notify({ tone: "success", title: ar ? "تم حفظ الدور" : "Role saved" });
      await load();
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to save role.");
    }
  };

  const togglePermission = (key: string, checked: boolean) => {
    if (!detail) return;
    setDetail({
      ...detail,
      permissions: checked
        ? [...new Set([...detail.permissions, key])]
        : detail.permissions.filter((item) => item !== key)
    });
  };

  return (
    <Stack>
      <Panel
        title={ar ? "الأدوار ومصفوفة الصلاحيات" : "Roles & Permission Matrix"}
        description={ar ? "أدوار نظام محمية وأدوار مخصصة مع إبطال الجلسات عند تغيير الصلاحيات." : "Protected system roles and custom roles with session revocation after permission changes."}
        actions={canManage ? <Button onClick={createNew}>{ar ? "دور جديد" : "New role"}</Button> : null}
      >
        {error ? <Alert tone="danger" title={ar ? "تعذر التنفيذ" : "Action failed"}>{error}</Alert> : null}
        <div className="roles-layout">
          <aside className="roles-list">
            {roles.map((role) => (
              <button key={role.id} type="button" className={detail?.role.id === role.id ? "is-active" : ""} onClick={() => void select(role)}>
                <span><strong>{ar ? role.nameAr : role.nameEn}</strong><small>{role.code} · {role.permissionCount} {ar ? "صلاحية" : "permissions"}</small></span>
                {role.isSystem ? <StatusBadge tone="info">{ar ? "نظام" : "System"}</StatusBadge> : null}
              </button>
            ))}
          </aside>
          <div>
            {!detail ? <EmptyState title={ar ? "اختر دورًا" : "Select a role"} /> : (
              <Stack>
                <Card
                  title={newRole ? (ar ? "دور مخصص جديد" : "New custom role") : (ar ? detail.role.nameAr : detail.role.nameEn)}
                  actions={canManage ? <Button onClick={() => void save()}>{ar ? "حفظ الدور" : "Save role"}</Button> : null}
                >
                  <Grid columns={2}>
                    <Field label={ar ? "الرمز" : "Code"} value={detail.role.code} disabled={detail.role.isSystem} onChange={(e) => setDetail({ ...detail, role: { ...detail.role, code: e.target.value.toUpperCase() } })} />
                    <Switch label={ar ? "الدور نشط" : "Role active"} checked={detail.role.isActive} disabled={!canManage || detail.role.code === "OWNER"} onChange={(e) => setDetail({ ...detail, role: { ...detail.role, isActive: e.target.checked } })} />
                    <Field label={ar ? "الاسم العربي" : "Arabic name"} value={detail.role.nameAr} disabled={!canManage} onChange={(e) => setDetail({ ...detail, role: { ...detail.role, nameAr: e.target.value } })} />
                    <Field label={ar ? "الاسم الإنجليزي" : "English name"} value={detail.role.nameEn} disabled={!canManage} onChange={(e) => setDetail({ ...detail, role: { ...detail.role, nameEn: e.target.value } })} />
                    <TextAreaField label={ar ? "الوصف العربي" : "Arabic description"} value={detail.role.descriptionAr ?? ""} disabled={!canManage} onChange={(e) => setDetail({ ...detail, role: { ...detail.role, descriptionAr: e.target.value || null } })} />
                    <TextAreaField label={ar ? "الوصف الإنجليزي" : "English description"} value={detail.role.descriptionEn ?? ""} disabled={!canManage} onChange={(e) => setDetail({ ...detail, role: { ...detail.role, descriptionEn: e.target.value || null } })} />
                  </Grid>
                </Card>
                {groups.map((group) => (
                  <Card key={group.groupKey} title={ar ? group.labelAr : group.labelEn}>
                    <div className="permission-grid">
                      {group.permissions.map((permission) => (
                        <Checkbox
                          key={permission.permissionKey}
                          label={ar ? permission.labelAr : permission.labelEn}
                          description={`${permission.permissionKey} · ${permission.riskLevel}`}
                          checked={detail.permissions.includes(permission.permissionKey)}
                          disabled={!canManage}
                          onChange={(e) => togglePermission(permission.permissionKey, e.target.checked)}
                        />
                      ))}
                    </div>
                  </Card>
                ))}
              </Stack>
            )}
          </div>
        </div>
      </Panel>
    </Stack>
  );
}

function AccessPanel({
  language,
  canManageUsers,
  canManageRoles,
  canManageSecurity,
  onChanged
}: {
  language: Language;
  canManageUsers: boolean;
  canManageRoles: boolean;
  canManageSecurity: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const ar = language === "ar";
  const [users, setUsers] = useState<UserAccess[]>([]);
  const [roles, setRoles] = useState<RoleSummary[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [selected, setSelected] = useState<UserAccess | null>(null);
  const [password, setPassword] = useState("");
  const [pin, setPin] = useState("");
  const [roleId, setRoleId] = useState("");
  const [branchId, setBranchId] = useState("");
  const [mfaEnrollment, setMfaEnrollment] = useState<Awaited<ReturnType<typeof startMfaEnrollment>> | null>(null);
  const [mfaCode, setMfaCode] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const [userItems, roleItems, branchItems] = await Promise.all([getUsers(), getRoles(), getBranches()]);
      setUsers(userItems);
      setRoles(roleItems.filter((role) => role.isActive));
      setBranches(branchItems);
      if (selected) setSelected(userItems.find((item) => item.userId === selected.userId) ?? null);
      setRoleId((value) => value || roleItems[0]?.id || "");
      setBranchId((value) => value || branchItems[0]?.id || "");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to load access.");
    }
  };
  useEffect(() => { void load(); }, []);

  const action = async (callback: () => Promise<unknown>, message: string, afterSuccess?: () => void) => {
    try {
      await callback();
      afterSuccess?.();
      toast.notify({ tone: "success", title: message });
      await load();
      onChanged();
    } catch (caught) {
      setError(getTeamAccessErrorMessage(caught, ar, "تعذر تنفيذ العملية.", "Action failed."));
    }
  };

  return (
    <Panel
      title={ar ? "الحسابات والوصول" : "Accounts & Access"}
      description={ar ? "حساب المستخدم، حالة الدخول، الأدوار حسب الفرع، PIN وMFA." : "User account, access state, branch roles, PIN and MFA."}
    >
      <Stack>
        {error ? <Alert tone="danger" title={ar ? "تعذر التنفيذ" : "Action failed"}>{error}</Alert> : null}
        <DataTable
          rows={users}
          rowKey={(row) => row.userId}
          columns={[
            { key: "user", header: ar ? "المستخدم" : "User", cell: (row) => <button className="team-table-link" onClick={() => { setError(""); setPassword(""); setPin(""); setMfaCode(""); setMfaEnrollment(null); setSelected(row); }}><strong>{row.displayName}</strong><small>{row.email}</small></button> },
            { key: "state", header: ar ? "الحالة" : "State", cell: (row) => <StatusBadge tone={row.isActive ? "success" : "danger"}>{row.isActive ? (ar ? "نشط" : "Active") : (ar ? "متوقف" : "Inactive")}</StatusBadge> },
            { key: "mfa", header: "MFA", cell: (row) => <StatusBadge tone={row.mfaEnabled ? "success" : "warning"}>{row.mfaMethod}</StatusBadge> },
            { key: "pin", header: "PIN", cell: (row) => <StatusBadge tone={row.hasPin ? "success" : "warning"}>{row.hasPin ? (ar ? "مضبوط" : "Set") : (ar ? "غير مضبوط" : "Missing")}</StatusBadge> },
            { key: "roles", header: ar ? "الوصول" : "Access", cell: (row) => row.assignments.map((assignment) => `${assignment.branchName}: ${ar ? assignment.roleNameAr : assignment.roleNameEn}`).join(" · ") },
            { key: "sessions", header: ar ? "الجلسات" : "Sessions", cell: (row) => row.activeSessionCount }
          ]}
        />

        <Dialog
          open={Boolean(selected)}
          onClose={() => { setSelected(null); setPassword(""); setPin(""); setMfaCode(""); setMfaEnrollment(null); setError(""); }}
          title={selected?.displayName ?? ""}
          description={selected?.email}
          footer={<Button variant="secondary" onClick={() => { setSelected(null); setPassword(""); setPin(""); setMfaCode(""); setMfaEnrollment(null); setError(""); }}>{ar ? "إغلاق" : "Close"}</Button>}
        >
          {selected ? (
            <Stack>
              {error ? <Alert tone="danger" title={ar ? "تعذر تنفيذ العملية" : "Action failed"}>{error}</Alert> : null}
              <Card title={ar ? "حالة الحساب" : "Account status"}>
                <Inline>
                  <StatusBadge tone={selected.isActive ? "success" : "danger"}>{selected.isActive ? "Active" : "Inactive"}</StatusBadge>
                  <StatusBadge tone={selected.lockedUntil ? "danger" : "success"}>{selected.lockedUntil ? (ar ? "مقفل" : "Locked") : (ar ? "غير مقفل" : "Unlocked")}</StatusBadge>
                  <StatusBadge tone={selected.mustChangePassword ? "warning" : "neutral"}>{selected.mustChangePassword ? (ar ? "تغيير كلمة المرور مطلوب" : "Password change required") : (ar ? "كلمة المرور مستقرة" : "Password stable")}</StatusBadge>
                </Inline>
                {canManageUsers ? (
                  <Button variant={selected.isActive ? "danger" : "secondary"} onClick={() => void action(() => setUserStatus(selected, !selected.isActive), ar ? "تم تحديث حالة الحساب" : "Account status updated")}>
                    {selected.isActive ? (ar ? "تعطيل الحساب" : "Deactivate account") : (ar ? "تفعيل الحساب" : "Activate account")}
                  </Button>
                ) : null}
              </Card>

              <Card title={ar ? "أدوار الفروع" : "Branch roles"}>
                <Stack>
                  {selected.assignments.map((assignment) => (
                    <div className="assignment-row" key={assignment.id}>
                      <span><strong>{assignment.branchName}</strong><small>{ar ? assignment.roleNameAr : assignment.roleNameEn}</small></span>
                      {canManageRoles ? <Button size="sm" variant="danger" onClick={() => void action(() => deleteUserRoleAssignment(selected.userId, assignment.id), ar ? "تم حذف الوصول" : "Access removed")}>{ar ? "حذف" : "Remove"}</Button> : null}
                    </div>
                  ))}
                  {canManageRoles ? (
                    <Grid columns={2}>
                      <SelectField label={ar ? "الفرع" : "Branch"} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                        {branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>)}
                      </SelectField>
                      <SelectField label={ar ? "الدور" : "Role"} value={roleId} onChange={(e) => setRoleId(e.target.value)}>
                        {roles.map((role) => <option key={role.id} value={role.id}>{ar ? role.nameAr : role.nameEn}</option>)}
                      </SelectField>
                      <Button onClick={() => void action(() => assignUserRole(selected.userId, { roleId, branchId, isActive: true, validFrom: null, validUntil: null }), ar ? "تم تحديث الدور" : "Role assignment updated")}>{ar ? "تعيين الدور" : "Assign role"}</Button>
                    </Grid>
                  ) : null}
                </Stack>
              </Card>

              {canManageSecurity ? (
                <Card title={ar ? "ضوابط الأمان" : "Security controls"}>
                  <Stack>
                    <Grid columns={2}>
                      <SecretField label={ar ? "كلمة مرور مؤقتة" : "Temporary password"} value={password} onChange={(e) => setPassword(e.target.value)} revealLabel={ar ? "إظهار كلمة المرور المؤقتة" : "Show temporary password"} concealLabel={ar ? "إخفاء كلمة المرور المؤقتة" : "Hide temporary password"} />
                      <Button onClick={() => void action(() => resetUserPassword(selected.userId, password), ar ? "تمت إعادة تعيين كلمة المرور" : "Password reset", () => setPassword(""))}>{ar ? "إعادة تعيين كلمة المرور" : "Reset password"}</Button>
                      <SecretField label="PIN" value={pin} inputMode="numeric" autoComplete="off" onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 8))} revealLabel={ar ? "إظهار PIN" : "Show PIN"} concealLabel={ar ? "إخفاء PIN" : "Hide PIN"} />
                      <Button onClick={() => {
                        const pinError = validateNewPin(pin, language);
                        if (pinError) {
                          setError(pinError);
                          return;
                        }
                        setError("");
                        void action(() => resetUserPin(selected.userId, pin.trim()), ar ? "تم ضبط PIN" : "PIN reset", () => setPin(""));
                      }}>{ar ? "ضبط PIN" : "Reset PIN"}</Button>
                    </Grid>
                    <small className="team-subtext">
                      {ar
                        ? "PIN من 4 إلى 8 أرقام، ولا يُسمح بالأرقام المتكررة أو المتسلسلة مثل 1111 أو 1234."
                        : "PIN must be 4–8 digits; repeated or sequential values such as 1111 or 1234 are not allowed."}
                    </small>

                    {!mfaEnrollment ? (
                      <Inline>
                        <Button variant="secondary" onClick={async () => setMfaEnrollment(await startMfaEnrollment(selected.userId))}>{ar ? "بدء إعداد TOTP" : "Start TOTP enrollment"}</Button>
                        {selected.mfaEnabled ? <Button variant="danger" onClick={() => void action(() => resetUserMfa(selected.userId, "Administrator reset from security center."), ar ? "تمت إعادة ضبط MFA" : "MFA reset")}>{ar ? "إعادة ضبط MFA" : "Reset MFA"}</Button> : null}
                      </Inline>
                    ) : (
                      <Alert tone="info" title={ar ? "إعداد تطبيق المصادقة" : "Authenticator setup"}>
                        <Stack>
                          <code>{mfaEnrollment.secret}</code>
                          <small>{mfaEnrollment.otpauthUri}</small>
                          <Alert
                            tone="warning"
                            title={ar ? "احفظ رموز الاسترداد" : "Save recovery codes"}
                          >
                            <code>{mfaEnrollment.recoveryCodes.join(" · ")}</code>
                          </Alert>
                          <Field label={ar ? "رمز التحقق" : "Verification code"} value={mfaCode} onChange={(e) => setMfaCode(e.target.value)} />
                          <Button onClick={() => void action(() => verifyMfaEnrollment(selected.userId, mfaEnrollment, mfaCode), ar ? "تم تفعيل MFA" : "MFA enabled", () => { setMfaEnrollment(null); setMfaCode(""); })}>{ar ? "تحقق وفعّل" : "Verify & enable"}</Button>
                        </Stack>
                      </Alert>
                    )}
                  </Stack>
                </Card>
              ) : null}
            </Stack>
          ) : null}
        </Dialog>
      </Stack>
    </Panel>
  );
}

function SessionsPanel({
  language,
  canManage,
  onChanged
}: {
  language: Language;
  canManage: boolean;
  onChanged: () => void;
}) {
  const toast = useToast();
  const ar = language === "ar";
  const [sessions, setSessions] = useState<SecuritySession[]>([]);
  const [error, setError] = useState("");

  const load = async () => {
    try { setSessions(await getSecuritySessions()); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "Unable to load sessions."); }
  };
  useEffect(() => { void load(); }, []);

  const revoke = async (session: SecuritySession) => {
    try {
      await revokeSecuritySession(session.id, "Revoked from security center.");
      toast.notify({ tone: "success", title: ar ? "تم إلغاء الجلسة" : "Session revoked" });
      await load();
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Unable to revoke session.");
    }
  };

  return (
    <Panel
      title={ar ? "الجلسات والأجهزة" : "Sessions & Devices"}
      description={ar ? "جلسات قابلة للإلغاء من الخادم مع آخر نشاط ومدة انتهاء." : "Server-revocable sessions with activity and expiry evidence."}
    >
      <Stack>
        {error ? <Alert tone="danger" title={ar ? "تعذر التنفيذ" : "Action failed"}>{error}</Alert> : null}
        <DataTable
          rows={sessions}
          rowKey={(row) => row.id}
          columns={[
            { key: "user", header: ar ? "المستخدم" : "User", cell: (row) => <span><strong>{row.userDisplayName}</strong><small className="team-subtext">{row.userEmail}</small></span> },
            { key: "context", header: ar ? "السياق" : "Context", cell: (row) => `${row.branchName} · ${row.roleName}` },
            { key: "device", header: ar ? "الجهاز" : "Device", cell: (row) => <span>{row.deviceName ?? "—"}<small className="team-subtext">{row.ipAddress ?? "—"}</small></span> },
            { key: "seen", header: ar ? "آخر نشاط" : "Last seen", cell: (row) => new Date(row.lastSeenAt).toLocaleString(ar ? "ar-AE" : "en-AE") },
            { key: "state", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.status === "Active" ? "success" : "neutral"}>{row.status}{row.isCurrentSession ? ` · ${ar ? "الحالية" : "Current"}` : ""}</StatusBadge> },
            { key: "action", header: "", cell: (row) => canManage && row.status === "Active" && !row.isCurrentSession ? <Button size="sm" variant="danger" onClick={() => void revoke(row)}>{ar ? "إلغاء" : "Revoke"}</Button> : null }
          ]}
        />
      </Stack>
    </Panel>
  );
}

function ApprovalsGovernancePanel({
  language,
  context,
  canManagePolicies,
  onOpen,
  onChanged
}: {
  language: Language;
  context: ContextOption;
  canManagePolicies: boolean;
  onOpen: () => void;
  onChanged: () => void;
}) {
  const toast = useToast();
  const ar = language === "ar";
  const [policies, setPolicies] = useState<ApprovalPolicy[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [currency, setCurrency] = useState("AED");
  const [editor, setEditor] = useState<Partial<ApprovalPolicy> | null>(null);
  const [error, setError] = useState("");
  const [policiesLoaded, setPoliciesLoaded] = useState(false);

  const operations: ApprovalOperation[] = [
    "DiscountOverride",
    "Refund",
    "VoidItem",
    "StockAdjustment",
    "PurchaseOrder",
    "DataExport",
    "ManualPrice",
    "SensitiveSettingPublish",
    "PreOrderReschedule",
    "PreOrderCancellation"
  ];

  const operationLabel = (operation: ApprovalOperation) => {
    const labels: Record<ApprovalOperation, [string, string]> = {
      DiscountOverride: ["تجاوز الخصم", "Discount override"],
      Refund: ["الاسترجاع", "Refund"],
      VoidItem: ["إلغاء صنف", "Void item"],
      StockAdjustment: ["تسوية المخزون", "Stock adjustment"],
      PurchaseOrder: ["أمر الشراء", "Purchase order"],
      DataExport: ["تصدير البيانات", "Data export"],
      ManualPrice: ["السعر اليدوي", "Manual price"],
      SensitiveSettingPublish: ["نشر إعداد حساس", "Sensitive setting publish"],
      PreOrderReschedule: ["إعادة جدولة الطلب المسبق", "Pre-order reschedule"],
      PreOrderCancellation: ["إلغاء الطلب المسبق", "Pre-order cancellation"]
    };
    return ar ? labels[operation][0] : labels[operation][1];
  };

  const load = async () => {
    setPoliciesLoaded(false);
    try {
      const [policyItems, branchItems, groups, runtime] = await Promise.all([
        getApprovalPolicies(),
        canManagePolicies ? getBranches().catch(() => []) : Promise.resolve([]),
        canManagePolicies ? getPermissionGroups().catch(() => []) : Promise.resolve([]),
        getRuntimeSettings(context.branchId).catch(() => null)
      ]);
      setPolicies(policyItems);
      setBranches(branchItems);
      setPermissionGroups(groups);
      if (runtime?.currency) setCurrency(runtime.currency);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setPoliciesLoaded(true);
    }
  };

  useEffect(() => {
    void load();
  }, [context.branchId]);

  const allPermissions = permissionGroups.flatMap((group) => group.permissions);

  const save = async () => {
    if (!editor) return;
    const operation = (editor.operation ?? "DiscountOverride") as ApprovalOperation;
    const requiredPermission = editor.requiredPermission ?? allPermissions[0]?.permissionKey ?? "approvals.decide";
    try {
      await saveApprovalPolicy({
        ...editor,
        operation,
        requiredPermission,
        expiresAfterMinutes: editor.expiresAfterMinutes ?? 15,
        requireReason: editor.requireReason ?? true,
        isEnabled: editor.isEnabled ?? true
      });
      setEditor(null);
      setError("");
      toast.notify({ tone: "success", title: ar ? "تم حفظ سياسة الموافقة" : "Approval policy saved" });
      await load();
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  const newPolicy = () => {
    if (!policiesLoaded) return;
    const used = new Set(policies.filter((item) => item.branchId == null).map((item) => item.operation));
    const operation = operations.find((item) => !used.has(item));
    if (!operation) {
      setError(ar ? "توجد بالفعل سياسة عامة لكل عملية. عدّل سياسة قائمة أو أنشئ سياسة مخصصة لفرع." : "A global policy already exists for every operation. Edit an existing policy or create a branch-scoped policy.");
      return;
    }
    setEditor({
      branchId: null,
      operation,
      isEnabled: true,
      thresholdMinor: null,
      thresholdBasisPoints: null,
      requiredPermission: "approvals.decide",
      expiresAfterMinutes: 15,
      requireReason: true
    });
  };

  return (
    <Stack>
      {error ? <Alert tone="danger" title={ar ? "تعذر تنفيذ العملية" : "Action failed"}>{error}</Alert> : null}
      <Panel
        data-testid="approval-policies-panel"
        title={ar ? "سياسات الموافقة" : "Approval Policies"}
        description={ar ? "قواعد مركزية تحدد العملية والفرع والحد المالي أو النسبي والصلاحية المطلوبة ومدة صلاحية الطلب." : "Central rules for operation, branch scope, financial/percentage thresholds, required permission and approval expiry."}
        actions={canManagePolicies ? <Button data-testid="new-approval-policy" disabled={!policiesLoaded} onClick={newPolicy}>{ar ? "سياسة جديدة" : "New policy"}</Button> : null}
      >
        {policies.length ? <DataTable
          rows={policies}
          rowKey={(row) => row.id}
          columns={[
            { key: "operation", header: ar ? "العملية" : "Operation", cell: (row) => <button className="team-link" onClick={() => canManagePolicies && setEditor(row)}><strong>{operationLabel(row.operation)}</strong><small className="team-subtext">{row.branchName ?? (ar ? "كل الفروع" : "All branches")}</small></button> },
            { key: "threshold", header: ar ? "الحد" : "Threshold", cell: (row) => row.thresholdMinor != null ? formatMoneyMinor(row.thresholdMinor, currency, ar ? "ar-AE" : "en-AE") : row.thresholdBasisPoints != null ? `${row.thresholdBasisPoints / 100}%` : "—" },
            { key: "permission", header: ar ? "الصلاحية المطلوبة" : "Required permission", cell: (row) => <code>{row.requiredPermission}</code> },
            { key: "expiry", header: ar ? "المدة" : "Expiry", cell: (row) => `${row.expiresAfterMinutes} ${ar ? "دقيقة" : "min"}` },
            { key: "status", header: ar ? "الحالة" : "Status", cell: (row) => <StatusBadge tone={row.isEnabled ? "success" : "neutral"}>{row.isEnabled ? (ar ? "مفعلة" : "Enabled") : (ar ? "متوقفة" : "Disabled")}</StatusBadge> }
          ]}
        /> : <EmptyState title={ar ? "لا توجد سياسات موافقة" : "No approval policies"} description={ar ? "أنشئ سياسة لتطبيق حوكمة الموافقات على العمليات الحساسة." : "Create a policy to govern sensitive operations."} />}
      </Panel>

      <ProtectedApprovalsEntry language={language} context={context} onOpen={onOpen} />

      <Dialog open={Boolean(editor)} onClose={() => setEditor(null)} title={ar ? "سياسة الموافقة" : "Approval policy"} footer={<Inline><Button variant="secondary" onClick={() => setEditor(null)}>{ar ? "إلغاء" : "Cancel"}</Button><Button data-testid="save-approval-policy" disabled={!canManagePolicies} onClick={() => void save()}>{ar ? "حفظ" : "Save"}</Button></Inline>}>
        {editor ? <Stack>
          <Grid columns={2}>
            <SelectField label={ar ? "النطاق" : "Scope"} value={editor.branchId ?? ""} onChange={(event) => setEditor({ ...editor, branchId: event.target.value || null })}><option value="">{ar ? "كل الفروع" : "All branches"}</option>{branches.length ? branches.map((branch) => <option key={branch.id} value={branch.id}>{branch.name}</option>) : <option value={context.branchId}>{context.branchName}</option>}</SelectField>
            <SelectField label={ar ? "العملية" : "Operation"} value={editor.operation ?? "DiscountOverride"} onChange={(event) => setEditor({ ...editor, operation: event.target.value as ApprovalOperation })}>{operations.map((operation) => <option key={operation} value={operation}>{operationLabel(operation)}</option>)}</SelectField>
            {permissionGroups.length ? <SelectField label={ar ? "الصلاحية المطلوبة" : "Required permission"} value={editor.requiredPermission ?? "approvals.decide"} onChange={(event) => setEditor({ ...editor, requiredPermission: event.target.value })}>{permissionGroups.map((group) => <optgroup key={group.groupKey} label={ar ? group.labelAr : group.labelEn}>{group.permissions.map((permission) => <option key={permission.permissionKey} value={permission.permissionKey}>{ar ? permission.labelAr : permission.labelEn} · {permission.permissionKey}</option>)}</optgroup>)}</SelectField> : <Field label={ar ? "الصلاحية المطلوبة" : "Required permission"} value={editor.requiredPermission ?? "approvals.decide"} onChange={(event) => setEditor({ ...editor, requiredPermission: event.target.value })} />}
            <Field label={ar ? "انتهاء الطلب بالدقائق" : "Expiry minutes"} type="number" min={1} max={1440} value={editor.expiresAfterMinutes ?? 15} onChange={(event) => setEditor({ ...editor, expiresAfterMinutes: Number(event.target.value) })} />
            <MoneyField label={ar ? "الحد المالي" : "Money threshold"} valueMinor={editor.thresholdMinor ?? null} currency={currency} nullable onMinorChange={(value) => setEditor({ ...editor, thresholdMinor: value })} />
            <Field label={ar ? "الحد النسبي %" : "Percentage threshold %"} type="number" min={0} max={100} step="0.01" value={editor.thresholdBasisPoints == null ? "" : editor.thresholdBasisPoints / 100} onChange={(event) => setEditor({ ...editor, thresholdBasisPoints: event.target.value === "" ? null : Math.round(Number(event.target.value) * 100) })} />
            <Switch label={ar ? "سبب إلزامي" : "Reason required"} checked={editor.requireReason ?? true} onChange={(event) => setEditor({ ...editor, requireReason: event.target.checked })} />
            <Switch label={ar ? "السياسة مفعلة" : "Policy enabled"} checked={editor.isEnabled ?? true} onChange={(event) => setEditor({ ...editor, isEnabled: event.target.checked })} />
          </Grid>
        </Stack> : null}
      </Dialog>
    </Stack>
  );
}

function ProtectedApprovalsEntry({
  language,
  context,
  onOpen
}: {
  language: Language;
  context: ContextOption;
  onOpen: () => void;
}) {
  const ar = language === "ar";
  return (
    <Stack>
      <Panel
        title={ar ? "موافقات المدير المحمية" : "Protected Manager Approvals"}
        description={
          ar
            ? `${context.branchName} · تُراجع طلبات الإهلاك والاسترجاع في المركز الإداري المخصص.`
            : `${context.branchName} · Void and refund requests are reviewed in the dedicated manager control center.`
        }
      >
        <Stack>
          <Alert
            tone="warning"
            title={ar ? "الرمز السري مطلوب" : "Manager PIN required"}
          >
            {ar
              ? "لمنع تجاوز فصل المهام، لم تعد قائمة الطلبات أو أزرار القرار تظهر داخل شاشة الفريق مباشرة. اضغط فتح المركز، ثم أدخل رمز المدير ليقوم الخادم بإصدار صلاحية مؤقتة مرتبطة بالحساب والفرع والجلسة."
              : "To preserve separation of duties, the request queue and decision controls are no longer exposed directly inside Team & Security. Open the dedicated center and verify the manager PIN to receive a short-lived, server-bound access grant."}
          </Alert>
          <Inline>
            <Button type="button" onClick={onOpen}>
              {ar ? "فتح موافقات المدير" : "Open Manager Approvals"}
            </Button>
          </Inline>
        </Stack>
      </Panel>
    </Stack>
  );
}
